import { FileShortInfo } from '@api';
import { bytesToHuman, useModal } from '@common';
import { IconButton, Space } from '@components';
import { DownloadFileIcon, FileTypeDefaultIcon,
	FileTypeDocIcon, FileTypeXlsIcon, FileTypePptIcon, FileTypePdfIcon, 
	TrashIcon } from '@icons';
import { DeleteFileModal } from '@modals';
import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import './FileView.scss';


interface FileViewProps {
	fileInfos: FileShortInfo[];
    onDownload: (filename: string) => void;
	onDelete: (filename: string) => void;
	onUpload: (files: File[]) => void;
	canEditFiles: boolean;
}

function FileView(props: FileViewProps): JSX.Element {
	const {
		fileInfos,
        onDownload,
		onDelete,
		onUpload,
		canEditFiles,
	} = props;

	// const canEditFiles = false;

	const {
		isModalOpen: isDeleteFileModalOpen,
		closeModal: closeDeleteFileModal,
		openModal: openDeleteFileModal,
	} = useModal();

	const [selectedFileName, setSelectedFileName] = useState<string>(null);

	const onDeleteClick = (name: string) => {
		setSelectedFileName(name);
		openDeleteFileModal();
	}

	
    const fileLinks = fileInfos.map((fileInfo) => {
		let icon = <FileTypeDefaultIcon className='fileItem__fileType' />;
		const name = fileInfo.name;
		const nameParts = name.split('.');
		const extension = nameParts[nameParts.length-1];

		if (extension == "pdf") {
			icon = <FileTypePdfIcon className='fileItem__fileType' />;
		} else if (extension == "doc" || extension == "docx") {
			icon = <FileTypeDocIcon className='fileItem__fileType' />;
		} else if (extension == "xls" || extension == "xlsx") {
			icon = <FileTypeXlsIcon className='fileItem__fileType' />;
		} else if (extension == "ppt" || extension == "pptx") {
			icon = <FileTypePptIcon className='fileItem__fileType' />;
		}

        return <div className="fileItem" key={name}>
			{icon}
			<div className='fileItem__title'>{name}</div>
			<div className='fileItem__trailing'>
				<div className='fileItem__size'>{bytesToHuman(fileInfo.size)}</div>
				<Space>
					<IconButton
						className='fileItem__action'
						icon={<DownloadFileIcon />}
						iconSize="24px" 
						size="nogap"
						onClick={() => onDownload(name)}
					/>
					{canEditFiles && <IconButton
						className='fileItem__action'
						icon={<TrashIcon />}
						iconSize="24px" 
						size="nogap"
						onClick={() => onDeleteClick(name)}
					/>}
				</Space>
			</div>
		</div>
    });

	const deleteFileModalView = useMemo((): JSX.Element => {
		return isDeleteFileModalOpen && (
			<DeleteFileModal
				closeModal={closeDeleteFileModal}
				fileName={selectedFileName}
				onSubmit={onDelete}
			/>
		);
	}, [closeDeleteFileModal, isDeleteFileModalOpen, selectedFileName, onDelete]);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		if (acceptedFiles && acceptedFiles.length) {
			onUpload(acceptedFiles);
		}
	}, [onUpload]);
	
	const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop});

	const fileDropView = useMemo(() => {
		let dropClasses = 'files__dropzone files__dropzone__text';
		if (isDragActive) {
			dropClasses += " dragging";
		}
		return canEditFiles && <div {...getRootProps()} className='files__dropzone_wrapper'>
				<input {...getInputProps()}/>
				<div className={dropClasses}>Перетащите сюда документ или нажмите, чтобы выбрать его...</div>
			</div>;
	}, [getRootProps, getInputProps, isDragActive, canEditFiles]);

	return (
		<div className="files">
			<div className='files__list'>{fileLinks}</div>
			{fileDropView}
			{deleteFileModalView}
		</div>
	);
}

const MemoisedFileView = React.memo(FileView);

export {MemoisedFileView as FileView};
export type {FileViewProps};